import { Component, OnInit, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { lastValueFrom, map } from 'rxjs';
import { FinancialControllerService } from 'src/app/admin-api/api/financialController.service';
import { Gateway } from 'src/app/admin-api/model/gateway';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { StorageService } from 'src/app/services/auth/storage.service';
import { SetupConfigurationService } from 'src/app/services/setup-configuration.service';
import { environment } from 'src/environments/environment';

/** Prod: MP 14 + Pagar.me 31. Stage: MP 12 + Pagar.me 29. */
const CHECKOUT_GATEWAY_IDS = environment.production
  ? new Set([14, 31])
  : new Set([12, 29]);

@Component({
  selector: 'app-checkout-gateway-priority',
  templateUrl: './checkout-gateway-priority.component.html',
  styleUrl: './checkout-gateway-priority.component.scss',
  providers: [MessageService]
})
export class CheckoutGatewayPriorityComponent implements OnInit {
  private title = inject(Title);
  private setupConfigurationService = inject(SetupConfigurationService);
  private financialService = inject(FinancialControllerService);
  private messageService = inject(MessageService);

  gatewayList: Array<Gateway & { label: string }> = [];
  primaryGatewayId: number | null = null;
  secondaryGatewayId: number | null = null;
  primaryGatewayLabel = '';
  secondaryGatewayLabel = '';
  saving = false;
  lastUpdatedBy: string | null = null;
  lastUpdatedAt: string | null = null;

  async ngOnInit() {
    this.title.setTitle('Ordem Gateway Checkout | Novas assinaturas');
    await this.load();
  }

  invertOrder() {
    if (this.primaryGatewayId == null || this.secondaryGatewayId == null) {
      return;
    }

    [this.primaryGatewayId, this.secondaryGatewayId] = [
      this.secondaryGatewayId,
      this.primaryGatewayId
    ];
    this.syncGatewayLabels();
  }

  async load() {
    LoaderService.showLoader();
    try {
      const [gateways, priorities] = await Promise.all([
        lastValueFrom(
          this.financialService
            .findGatewaysList()
            .pipe(map((response) => response.result ?? []))
        ),
        lastValueFrom(this.setupConfigurationService.getCheckoutGatewayPriority())
      ]);

      this.gatewayList = (gateways ?? [])
        .filter(
          (gateway) =>
            gateway.gatewayId != null &&
            CHECKOUT_GATEWAY_IDS.has(gateway.gatewayId)
        )
        .map((gateway) => ({
          ...gateway,
          label: `${gateway.gatewayName ?? 'Gateway'} (${gateway.gatewayId})`
        }));

      const primary = priorities.find((item) => item.gatewayOrder === 1);
      const secondary = priorities.find((item) => item.gatewayOrder === 2);
      this.primaryGatewayId = primary?.gatewayId ?? null;
      this.secondaryGatewayId = secondary?.gatewayId ?? null;
      this.syncGatewayLabels();
      this.lastUpdatedBy = primary?.updatedBy ?? secondary?.updatedBy ?? null;
      this.lastUpdatedAt = primary?.dateUpdated ?? secondary?.dateUpdated ?? null;
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async save() {
    if (this.primaryGatewayId == null || this.secondaryGatewayId == null) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Selecione gateway primário e secundário.'
      });
      return;
    }
    if (this.primaryGatewayId === this.secondaryGatewayId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Primário e secundário devem ser diferentes.'
      });
      return;
    }

    this.saving = true;
    LoaderService.showLoader();
    try {
      const updatedBy =
        StorageService.userId != null ? `user-${StorageService.userId}` : 'admin';
      const result = await lastValueFrom(
        this.setupConfigurationService.updateCheckoutGatewayPriority(
          [this.primaryGatewayId, this.secondaryGatewayId],
          updatedBy
        )
      );
      const primary = result.find((item) => item.gatewayOrder === 1);
      const secondary = result.find((item) => item.gatewayOrder === 2);
      this.primaryGatewayId = primary?.gatewayId ?? this.primaryGatewayId;
      this.secondaryGatewayId = secondary?.gatewayId ?? this.secondaryGatewayId;
      this.syncGatewayLabels();
      this.lastUpdatedBy = primary?.updatedBy ?? secondary?.updatedBy ?? updatedBy;
      this.lastUpdatedAt = primary?.dateUpdated ?? secondary?.dateUpdated ?? null;
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Ordem de gateway do checkout atualizada.'
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      this.saving = false;
      LoaderService.showLoader(false);
    }
  }

  private syncGatewayLabels() {
    this.primaryGatewayLabel =
      this.gatewayList.find(
        (gateway) => gateway.gatewayId === this.primaryGatewayId
      )?.label ?? '';
    this.secondaryGatewayLabel =
      this.gatewayList.find(
        (gateway) => gateway.gatewayId === this.secondaryGatewayId
      )?.label ?? '';
  }
}
